/**
 * Procedure instances (RFC procedure-instance, Phase 2).
 *
 * The clinical events a team performs — each groups several independent
 * recordings. This is the list + create surface; a row opens the detail
 * page (team roster + grouped recordings). Tenant_admin.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { formatDate } from '../../util/datetime';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import { SortHeader } from '../../components/SortHeader';
import { sortRows, useTableSort } from '../../util/tableSort';
import { procedureColor } from '../../util/procedureColors';
import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as instancesApi from '../../api/procedureInstances';
import {
  INSTANCE_KINDS,
  INSTANCE_KIND_LABELS,
  INSTANCE_STATUSES,
  INSTANCE_STATUS_LABELS,
} from '../../api/procedureInstances';
import * as orgApi from '../../api/organizations';
import * as proceduresApi from '../../api/procedures';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { SearchableSelect } from '../../components/SearchableSelect';

const NONE = [{ value: '', label: '— none —' }];
const ALL_STATUSES = [{ value: '', label: 'All' }];
const STATUS_OPTIONS = INSTANCE_STATUSES.map((s) => ({
  value: s as string,
  label: INSTANCE_STATUS_LABELS[s],
}));
const KIND_OPTIONS = INSTANCE_KINDS.map((k) => ({
  value: k as string,
  label: INSTANCE_KIND_LABELS[k],
}));

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> =
  {
    scheduled: 'info',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'default',
    aborted: 'default',
  };

export function TenantProcedureInstances({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [rows, setRows] = useState<instancesApi.ProcedureInstance[] | null>(
    null,
  );
  const [departments, setDepartments] = useState<orgApi.Department[]>([]);
  const [locations, setLocations] = useState<orgApi.Location[]>([]);
  const [procedures, setProcedures] = useState<proceduresApi.Procedure[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [inst, d, l, procs] = await Promise.all([
        instancesApi.listInstances(
          tenantId,
          statusFilter ? { status: statusFilter } : {},
        ),
        orgApi.listDepartments(tenantId),
        orgApi.listLocations(tenantId),
        proceduresApi.listProcedures(tenantId),
      ]);
      setRows(inst);
      setDepartments(d);
      setLocations(l);
      setProcedures(procs);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const deptName = (id: string | null) =>
    id ? (departments.find((d) => d.id === id)?.name ?? '—') : '—';
  const locName = (id: string | null) =>
    id ? (locations.find((l) => l.id === id)?.name ?? '—') : '—';

  // Click-to-sort on every column. Default: newest first.
  const sort = useTableSort<
    'procedure' | 'status' | 'department' | 'location' | 'kind' | 'created'
  >('created', 'desc');

  const sortedRows = useMemo(() => {
    if (rows === null) return null;
    return sortRows(
      rows,
      (r) => {
        switch (sort.orderBy) {
          case 'procedure':
            return r.procedure_name;
          case 'status':
            return (
              INSTANCE_STATUS_LABELS[r.status as instancesApi.InstanceStatus] ??
              r.status
            );
          case 'department':
            return deptName(r.department_id);
          case 'location':
            return locName(r.location_id);
          case 'kind':
            return r.kind ? (INSTANCE_KIND_LABELS[r.kind] ?? r.kind) : null;
          case 'created':
            return r.created_at;
        }
      },
      sort.order,
    );
    // deptName/locName close over departments/locations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort.orderBy, sort.order, departments, locations]);

  // Create dialog.
  const [open, setOpen] = useState(false);
  const [procId, setProcId] = useState('');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('scheduled');
  const [dept, setDept] = useState('');
  const [loc, setLoc] = useState('');
  const [ref, setRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /** True when a facility belongs in the given department — its own
   *  department, or unassigned (a facility with no department is usable
   *  anywhere). An empty deptId means "no filter", so anything qualifies. */
  const facilityInDept = (locId: string, deptId: string): boolean => {
    if (!deptId) return true;
    const l = locations.find((x) => x.id === locId);
    return !!l && (l.department_id === deptId || l.department_id == null);
  };

  // Facilities offered in the create dialog, scoped to the chosen department
  // (plus any unassigned facilities) so the picker isn't a flat list of every
  // room across the tenant.
  const visibleLocations = dept
    ? locations.filter((l) => l.department_id === dept || l.department_id == null)
    : locations;

  const openCreate = () => {
    setProcId('');
    setKind('');
    setStatus('scheduled');
    setDept('');
    setLoc('');
    setRef('');
    setFormError(null);
    setOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    const proc = procedures.find((p) => p.id === procId);
    if (!proc) {
      setFormError('Pick a procedure.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const created = await instancesApi.createInstance(tenantId, {
        procedure_id: proc.id,
        procedure_name: proc.name,
        kind: kind || null,
        status,
        department_id: dept || null,
        location_id: loc || null,
        external_case_ref: ref.trim() || null,
      });
      setOpen(false);
      navigate(`/tenant/procedure-instances/${created.id}`);
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'failed to create instance'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return <Alert severity="warning">No tenant selected.</Alert>;
  }

  const actions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Lifecycle order (scheduled → completed …), not alphabetical. */}
      <SearchableSelect
        keepOrder
        label="Status"
        value={statusFilter}
        onChange={setStatusFilter}
        pinned={ALL_STATUSES}
        options={STATUS_OPTIONS}
        sx={{ minWidth: 150 }}
      />
      <RefreshButton onClick={() => void reload()} refreshing={loading} />
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={openCreate}
        disabled={!canWrite}
      >
        New instance
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={3}>
      {/* Hosted in the combined Procedures page: actions render into the tab
          bar's slot (same line as the tabs). */}
      {actionsSlot ? (
        createPortal(actions, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!embedded && (
            <Box>
              <Typography variant="h5">Procedure instances</Typography>
              <Typography variant="body2" color="text.secondary">
                The procedures your teams perform. Each instance groups the
                recordings + team members for one clinical event.
              </Typography>
            </Box>
          )}
          {actions}
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {rows === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No procedure instances yet. Click <strong>New instance</strong> to
            create one, or group recordings from the Activity page.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Procedure" col="procedure" sort={sort} />
                <SortHeader label="Status" col="status" sort={sort} />
                <SortHeader label="Department" col="department" sort={sort} />
                <SortHeader label="Facility" col="location" sort={sort} />
                <SortHeader label="Kind" col="kind" sort={sort} />
                <SortHeader label="Created" col="created" sort={sort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedRows ?? []).map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(`/tenant/procedure-instances/${r.id}`)
                  }
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: procedureColor(r.procedure_name),
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.procedure_name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        INSTANCE_STATUS_LABELS[
                          r.status as instancesApi.InstanceStatus
                        ] ?? r.status
                      }
                      color={STATUS_COLOR[r.status] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>{deptName(r.department_id)}</TableCell>
                  <TableCell>{locName(r.location_id)}</TableCell>
                  <TableCell>
                    {r.kind ? INSTANCE_KIND_LABELS[r.kind] ?? r.kind : '—'}
                  </TableCell>
                  <TableCell>
                    {formatDate(r.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>New procedure instance</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Create a clinical event — plan it now (status Scheduled) and
              attach recordings + team later, or group existing recordings
              into it.
            </DialogContentText>
            <Stack spacing={2}>
              <SearchableSelect
                label="Procedure"
                fullWidth
                size="medium"
                required
                value={procId}
                onChange={setProcId}
                disabled={submitting}
                helperText={
                  procedures.length === 0
                    ? 'No procedures yet — add them on the Procedures page first.'
                    : 'Pick from your procedures catalog.'
                }
                options={procedures.map((p) => ({
                  value: p.id,
                  label: `${p.name} · #${p.procedure_number}`,
                }))}
              />
              <Stack direction="row" spacing={2}>
                <SearchableSelect
                  label="Kind"
                  fullWidth
                  size="medium"
                  value={kind}
                  onChange={setKind}
                  disabled={submitting}
                  pinned={NONE}
                  options={KIND_OPTIONS}
                />
                {/* Lifecycle order, not alphabetical. */}
                <SearchableSelect
                  keepOrder
                  label="Status"
                  fullWidth
                  size="medium"
                  value={status}
                  onChange={setStatus}
                  disabled={submitting}
                  options={STATUS_OPTIONS}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <SearchableSelect
                  label="Department"
                  fullWidth
                  size="medium"
                  value={dept}
                  onChange={(next) => {
                    setDept(next);
                    // Drop a facility that isn't in the newly-chosen
                    // department so we never submit a mismatched pair.
                    if (loc && !facilityInDept(loc, next)) setLoc('');
                  }}
                  disabled={submitting}
                  pinned={NONE}
                  options={departments.map((d) => ({ value: d.id, label: d.name }))}
                />
                <SearchableSelect
                  label="Facility"
                  fullWidth
                  size="medium"
                  value={loc}
                  onChange={setLoc}
                  disabled={submitting}
                  helperText={
                    dept
                      ? 'Facilities in the selected department (plus any not assigned to one).'
                      : undefined
                  }
                  pinned={NONE}
                  options={visibleLocations.map((l) => ({ value: l.id, label: l.name }))}
                />
              </Stack>
              <TextField
                label="External case reference (optional)"
                fullWidth
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                disabled={submitting}
                helperText="An opaque case number to correlate later — no patient info."
              />
            </Stack>
            {formError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || procId === ''}
            >
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
}
